"use client";
import Script from 'next/script'
import { ThemeProvider } from '@mui/material/styles';
import "@/app/style/globals.scss";
import { generateUrl, getDefaultMenuTab, theme, isIOS } from "@/app/utils/Common";
import { LANGCODE_CT, LANGCODE_EN, LanguageProvider } from './utils/LanguageContext';
import { MyContextProvider } from './utils/MyContext';
import AdrumConfig from './utils/AdrumConfig';
import DataDogRumInit from './utils/DataDogConfig';
import { useParams, usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { getAssetPrefix } from './utils/Metadata';
import { pageSet } from './utils/constant/pageSet';
import { MenuTab } from "@/app/utils/class/common/base";
import { AppRouterCacheProvider } from '@mui/material-nextjs/v15-appRouter';

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const { language } = useParams();
  const [enableAppD, setEnableAppD] = useState(false);
  const [enableDataDog, setEnableDataDog] = useState(false);
  const paths = usePathname().split("/");
  const assetPrefix = getAssetPrefix();
  const isValidPage = [LANGCODE_EN, LANGCODE_CT].includes(paths[1] || '') && paths[2] === 'overseas' && (Object.values(pageSet).includes(paths[3] || '') || Object.values(pageSet).includes(new MenuTab(paths[3] || '').toString()));

  useEffect(() => { 
    if (!isValidPage) {
      const url = generateUrl(`/${encodeURIComponent(getDefaultMenuTab().valueOf())}`, {}, true)
      window.location.replace(url);
    } 
    
    // fix SQ0253-2030
    if(!isIOS()) {
      document.body.style.setProperty('overscroll-behavior-y', 'unset');
    }
  }, []);

  useEffect(() => { 
    setEnableAppD(window.globalConfig.OTHERS.ENABLE_APPDYNAMIC)
  }, []);

  useEffect(() => { 
    setEnableDataDog(window.globalConfig.OTHERS.ENABLE_DATADOG)
  }, []);
  return (
    <LanguageProvider>
      <>
      <head>
        <link rel="stylesheet" 
        href={language === "en-us" ? 'https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@300;400;500;700&display=swap' 
        : 'https://fonts.googleapis.com/css2?family=Noto+Sans+HK:wght@300;400;500;700&display=swap'}></link>
        {language === "en-us" ? <link href="https://fonts.googleapis.com/css2?family=Red+Hat+Text:ital,wght@0,300..700;1,300..700&display=swap" rel="stylesheet"></link> : null}
        <Script src={`${assetPrefix}/config/globalConfig.js`} strategy="beforeInteractive" />
        <Script src={`${assetPrefix}/js/utils.js`} data-asset-prefix={assetPrefix} strategy="beforeInteractive" /> 
        {/* <Script src={`${assetPrefix}/js/sso.min.js`} strategy="beforeInteractive" /> */}
        {enableAppD && <AdrumConfig />}
        {enableDataDog && <DataDogRumInit />}
      </head>
      <body className={language === "en-us" ? "fontFamilyEN" : "fontFamilyHK"}
        style={{
          '--url-swiper-before': `url(${assetPrefix}/swiper_before.svg)`,
          '--url-swiper-next': `url(${assetPrefix}/swiper_next.svg)`,
          '--url-seo-bg': `url(${assetPrefix}/footer/seo_bg.gif)`,
          '--url-seo-div': `url(${assetPrefix}/footer/seo_stroke.gif)`,
          '--url-seo-btn': `url(${assetPrefix}/footer/arrow-up.svg)`,
          '--url-ewin': `url(${assetPrefix}/commonImg/arrow_1.gif)`,
          '--url-headernav-main': `url(${assetPrefix}/headernav/nav-bg.jpg)`,
          '--url-headernav-sub': `url(${assetPrefix}/headernav/subnav_bg.gif)`,
          '--url-headernav-arrow': `url(${assetPrefix}/headernav/nav_arrow.gif)`,
          '--url-arrow-down': `url(${assetPrefix}/footer/arrow-up.svg)`,
          '--url-next-btn': `url(${assetPrefix}/nextdayrace_bg.png)`,
          '--url-photo-arrow': `url(${assetPrefix}/photos_arrow.svg)`,
          '--url-upcoming-swiper-before': `url(${assetPrefix}/upcoming_swiper_before.svg)`,
          '--url-upcoming-swiper-next': `url(${assetPrefix}/upcoming_swiper_next.svg)`,
          '--url-pattern-pc': `url(${assetPrefix}/pattern_pc.png)`,
          '--url-pattern-mobile': `url(${assetPrefix}/pattern_mobile.png)`,
        } as React.CSSProperties}>
          <MyContextProvider assetPrefix={assetPrefix}>
            <AppRouterCacheProvider options={{ key: 'css', prepend: true }}>
              <ThemeProvider theme={theme}>
                {children}
              </ThemeProvider>
            </AppRouterCacheProvider>
          </MyContextProvider>
      </body>
      </>
    </LanguageProvider>
  );
}